import { useEffect, useRef, useState } from 'react';

/**
 * Countdown decrescente da `secondsTotal` a 0, per il pattern `<UndoToast>`.
 * Quando arriva a 0 chiama `onExpire` una volta sola.
 *
 * - `active=false` → pausa/disarma. Il valore corrente resta congelato.
 * - Re-attivare con `active=true` riparte da `secondsTotal`.
 *
 * Pattern stateless: il consumer mantiene `active` in sync con la visibilità
 * del toast (es. `active={undoSnapshot !== null}`).
 */
export function useUndoCountdown(
  secondsTotal: number,
  active: boolean,
  onExpire: () => void,
): number {
  const [secondsLeft, setSecondsLeft] = useState<number>(secondsTotal);
  // Capture latest `onExpire` per evitare re-arm del setInterval ad ogni render
  // del consumer (callback inline frequente nel pattern <UndoToast>).
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;

  useEffect(() => {
    if (!active) {
      setSecondsLeft(secondsTotal);
      return;
    }
    setSecondsLeft(secondsTotal);
    const id = window.setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          window.clearInterval(id);
          onExpireRef.current();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      window.clearInterval(id);
    };
  }, [active, secondsTotal]);

  return secondsLeft;
}
